"use client";
import { RiArrowLeftLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import { Separator } from "@workspace/ui/components/separator";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect } from "react";
import {
	ArtistProfileCard,
	SpotifyEmbedCard,
} from "@/components/features/artist/cards";
import { ArtistContent } from "@/components/features/artist/content/artist-content";
import { ArtistProfileSkeleton } from "@/components/features/artist/skeletons";
import { parseArtistSlug } from "@/lib/features/artist/artist-utils";
import { trackArtistDiscovery } from "@/lib/services/analytics";
import { trpc } from "@/lib/trpc/react";

interface ArtistProfilePageClientProps {
	artistSlug: string;
}

function ArtistProfilePageClientInner({
	artistSlug,
}: ArtistProfilePageClientProps) {
	const router = useRouter();
	const searchParams = useSearchParams();

	// Parse the artist name from slug (supports both kebab-case and URL-encoded)
	const artistName = parseArtistSlug(artistSlug);

	// Fetch artist basic data using existing tRPC endpoint for header
	const {
		data: artistData,
		isLoading,
	} = trpc.artist.getProfileWithSets.useQuery(
		{
			artistName: artistName,
			timeFilter: "all",
		},
		{
			retry: false,
		},
	);

	// Track artist view on mount when we have artist data
	useEffect(() => {
		if (artistData?.artist) {
			// Determine the source based on the referrer
			const fromParam = searchParams?.get("from");
			let source: "festival" | "search" | "discover" | "profile" = "discover";

			if (fromParam === "search") {
				source = "search";
			} else if (fromParam === "festival") {
				source = "festival";
			} else if (fromParam === "profile") {
				source = "profile";
			}

			trackArtistDiscovery(
				artistData.artist.artist_id,
				artistData.artist.artist_name,
				source,
				"viewed",
			);
		}
	}, [artistData?.artist, searchParams]);

	const handleBack = useCallback(() => {
		// Check if coming from artist search
		const fromParam = searchParams?.get("from");
		if (fromParam === "search" || window.history.length > 1) {
			router.back();
		} else {
			router.push("/artists");
		}
	}, [router, searchParams]);

	if (isLoading) {
		return <ArtistProfileSkeleton />;
	}

	// Only show error state when no data is available at all
	// Don't show error if we have hydrated/cached data but a background refetch failed —
	// this prevents soft 404s where Google sees "Artist Not Found" on a valid HTTP 200 page
	if (!artistData?.artist && !isLoading) {
		return (
			<div className="container mx-auto max-w-3xl px-4 py-4">
				<div className="flex flex-col gap-3">
					<div className="flex items-center gap-2">
						<Button
							variant="secondary"
							size="sm"
							onClick={handleBack}
							className="flex items-center gap-1 text-sm"
						>
							<RiArrowLeftLine className="h-4 w-4" />
							<span>Back</span>
						</Button>
					</div>

					<Card className="border bg-card shadow-sm">
						<CardContent className="py-8 text-center">
							<h1 className="mb-2 font-bold text-2xl">Artist Not Found</h1>
							<p className="mb-4 text-muted-foreground">
								The requested artist could not be found or may have been
								removed.
							</p>
							<Button asChild>
								<Link href="/artists">Browse Artists</Link>
							</Button>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	const { artist, aliases } = artistData;
	const displayName = artist.artist_name;

	return (
		<div className="container mx-auto max-w-3xl px-4 py-4">
			<div className="flex flex-col gap-3">
				{/* Navigation */}
				<div className="flex items-center gap-2">
					<Button
						variant="secondary"
						size="sm"
						onClick={handleBack}
						className="flex items-center gap-1 text-sm"
					>
						<RiArrowLeftLine className="h-4 w-4" />
						<span>Back</span>
					</Button>
					<Separator className="h-4" orientation="vertical" />
					<span className="font-medium text-sm">{displayName}</span>
				</div>

				{/* Artist Profile Card */}
				<ArtistProfileCard
					artist={artist}
					priority={true}
					aliases={aliases}
				/>

				{/* Spotify Embed Card - uses spotify_id for artist embed */}
				{artist.spotify_id && (
					<SpotifyEmbedCard
						spotifyId={artist.spotify_id}
						artistName={artist.artist_name}
					/>
				)}

				{/* Enhanced Artist Content - festivals/sets/shows tabs */}
				<ArtistContent
					artistId={artist.artist_id}
					artistName={artist.artist_name}
					artistSlug={artistSlug}
				/>
			</div>
		</div>
	);
}

export function ArtistProfilePageClient(props: ArtistProfilePageClientProps) {
	return (
		<Suspense fallback={<ArtistProfileSkeleton />}>
			<ArtistProfilePageClientInner {...props} />
		</Suspense>
	);
}
